// src/pages/Liquidaciones/LiquidacionDetalle.jsx
import { useEffect, useMemo, useState, useLayoutEffect, useRef } from "react";
import { Box, Typography, Button, Grid, useMediaQuery, Stack, Chip } from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Header from "../../../components/Header";
import { getLiquidacionById } from "../../../services/liquidacionService";
import { mapClosureToLiquidacion } from "../../../utils/liquidaciones";

/** ========== Paletas reutilizadas de TurnoDetalle ========== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};

const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/** ========== Componentes base (idénticos a TurnoDetalle) ========== */
function DataChip({ label, value, palette, highlight = false, compact = false }) {
  return (
    <Box
      sx={{
        p: compact ? 1.25 : 2,
        borderRadius: "10px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        minHeight: compact ? 56 : 76,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2, 6, 23, 0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: palette.label,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: compact ? 0.25 : 0.5,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: palette.text,
          fontWeight: 700,
          fontSize: compact ? 16 : 20,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function InfoCard({ title, children, palette }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "10px",
        p: 2,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2, 6, 23, 0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color: palette.label,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 1.5,
          fontWeight: 700,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function RowItem({ label, value, palette }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "10px",
        p: 1.5,
      }}
    >
      <Typography variant="body2" sx={{ color: palette.label, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: palette.text,
          fontWeight: 600,
          fontSize: { xs: "0.95rem", sm: "1rem" },
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* ============== FitColumn ============== */
function useFitScale() {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const update = () => {
      const ch = content.offsetHeight;
      const ph = container.clientHeight;
      const next = ch ? Math.min(1, ph / ch) : 1;
      setScale(next);
    };

    const ro = new ResizeObserver(update);
    ro.observe(container);
    ro.observe(content);

    const t = setTimeout(update, 0);

    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, []);

  return { containerRef, contentRef, scale };
}

function FitColumn({ children, disableScale = false }) {
  const { containerRef, contentRef, scale } = useFitScale();

  if (disableScale) {
    return (
      <Box
        ref={containerRef}
        sx={{
          height: { xs: "auto", lg: "100%" },
          minHeight: 0,
          overflowY: { xs: "visible", lg: "auto" },
          pr: { xs: 0, lg: 1 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 2,
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        overflow: "hidden",
        pr: 1,
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          width: scale < 1 ? `${100 / scale}%` : "100%",
          willChange: "transform",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** ========== Página ========== */
const LiquidacionDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = useMemo(() => (isDark ? PALETTE_DARK : PALETTE_LIGHT), [isDark]);
  const isMobile = useMediaQuery("(max-width:900px)");

  const initialLiquidacion = location.state?.liquidacion || null;
  const [liquidacion, setLiquidacion] = useState(initialLiquidacion);
  const [loading, setLoading] = useState(!initialLiquidacion);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getLiquidacionById(id);
        setLiquidacion(mapClosureToLiquidacion(data));
      } catch (e) {
        setError(e.message || "No se pudo cargar la liquidación");
      } finally {
        setLoading(false);
      }
    };

    if (!initialLiquidacion || initialLiquidacion.id !== id) {
      load();
    }
  }, [id, initialLiquidacion]);

  if (loading || !liquidacion) {
    return (
      <Typography sx={{ m: 2 }}>
        {error ? error : "Cargando liquidación..."}
      </Typography>
    );
  }

  const kpis = [
    { label: "Facturación", value: `€${Number(liquidacion["facturacion-liquidacion"] || 0).toFixed(2)}` },
    { label: "Tarjeta", value: `€${Number(liquidacion["tarjetas-liquidacion"] || 0).toFixed(2)}` },
    { label: "Efectivo", value: `€${Number(liquidacion["efectivo-liquidacion"] || 0).toFixed(2)}` },
    { label: "Gasolina", value: `€${Number(liquidacion["gasolina-liquidacion"] || 0).toFixed(2)}` },
    { label: "KM", value: `${Number(liquidacion["km-liquidacion"] || 0)} km` },
    { label: "Ganancias Conductor (pct)", value: `€${Number(liquidacion["ganancias-conductor"] || 0).toFixed(2)}`, highlight: true },
    { label: "Total Conductor (suma)", value: `€${Number(liquidacion["total-conductor-liquidacion"] || 0).toFixed(2)}`, highlight: true },
    { label: "Ganancia Empresa (pct)", value: `€${Number(liquidacion["ganancia-empresa"] || 0).toFixed(2)}` },
    { label: "Total Empresa (suma)", value: `€${Number(liquidacion["total-empresa-liquidacion"] || 0).toFixed(2)}`, highlight: true },
  ];

  const turnos = Array.isArray(liquidacion.raw?.turnos) ? liquidacion.raw.turnos : [];

  return (
    <Box
      sx={{
        mx: { xs: 0, md: "auto" },
        px: { xs: 2, md: 2.5 },
        pb: { xs: 4, md: "20px" },
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          color: palette.title,
          textTransform: "none",
          fontSize: "16px",
          fontWeight: 500,
          alignSelf: "flex-start",
          "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
        }}
      >
        Volver
      </Button>

      <Header title={`Liquidación #${id}`} subtitle="Resumen de la liquidación y hojas de turno" />

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
        {kpis.map((kpi) => (
          <Grid item xs={12} sm={6} md={4} key={kpi.label}>
            <DataChip
              label={kpi.label}
              value={kpi.value}
              palette={palette}
              highlight={kpi.highlight}
            />
          </Grid>
        ))}
      </Grid>

      {/* Cuerpo similar a TurnoDetalle */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: { xs: "block", lg: "grid" },
          gridTemplateColumns: { lg: "1fr 1fr" },
          columnGap: 4,
          alignItems: "stretch",
        }}
      >
        {/* Izquierda */}
        {isMobile ? (
          <Stack spacing={2}>
            <InfoCard title="Identificación" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Licencia" value={liquidacion.licencia} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="ID de Liquidación" value={String(liquidacion.id)} palette={palette} /></Grid>
                <Grid item xs={12}><RowItem label="Conductor" value={liquidacion.conductor} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Totales" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Facturación Total" value={`€${Number(liquidacion["facturacion-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Ganancia Empresa (pct)" value={`€${Number(liquidacion["ganancia-empresa"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Total Empresa (suma)" value={`€${Number(liquidacion["total-empresa-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Total Conductor (suma)" value={`€${Number(liquidacion["total-conductor-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Ganancias Conductor (pct)" value={`€${Number(liquidacion["ganancias-conductor"] || 0).toFixed(2)}`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Detalle" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Tarjetas" value={`€${Number(liquidacion["tarjetas-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Gasolina" value={`€${Number(liquidacion["gasolina-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Km liquidación" value={`${Number(liquidacion["km-liquidacion"] || 0)} km`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Nº Turnos" value={liquidacion["num-turnos"]} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Nº Servicios" value={liquidacion["num-servicios"]} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Fecha Primer Turno" value={liquidacion["fecha-primer-turno"]} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Fecha Último Turno" value={liquidacion["fecha-ultimo-turno"]} palette={palette} /></Grid>
              </Grid>
            </InfoCard>
          </Stack>
        ) : (
          <FitColumn disableScale={!isMobile}>
            <InfoCard title="Identificación" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Licencia" value={liquidacion.licencia} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="ID de Liquidación" value={String(liquidacion.id)} palette={palette} /></Grid>
                <Grid item xs={12}><RowItem label="Conductor" value={liquidacion.conductor} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Totales" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Facturación Total" value={`€${Number(liquidacion["facturacion-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Ganancia Empresa (pct)" value={`€${Number(liquidacion["ganancia-empresa"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Total Empresa (suma)" value={`€${Number(liquidacion["total-empresa-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Total Conductor (suma)" value={`€${Number(liquidacion["total-conductor-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Ganancias Conductor (pct)" value={`€${Number(liquidacion["ganancias-conductor"] || 0).toFixed(2)}`} palette={palette} /></Grid>
              </Grid>
            </InfoCard>

            <InfoCard title="Detalle" palette={palette}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}><RowItem label="Tarjetas" value={`€${Number(liquidacion["tarjetas-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Gasolina" value={`€${Number(liquidacion["gasolina-liquidacion"] || 0).toFixed(2)}`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Km liquidación" value={`${Number(liquidacion["km-liquidacion"] || 0)} km`} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Nº Turnos" value={liquidacion["num-turnos"]} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Nº Servicios" value={liquidacion["num-servicios"]} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Fecha Primer Turno" value={liquidacion["fecha-primer-turno"]} palette={palette} /></Grid>
                <Grid item xs={12} sm={6}><RowItem label="Fecha Último Turno" value={liquidacion["fecha-ultimo-turno"]} palette={palette} /></Grid>
              </Grid>
            </InfoCard>
          </FitColumn>
        )}

        {/* Derecha: lista de turnos */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 0,
            height: { xs: "auto", lg: "100%" },
            pb: { xs: 2, md: 3 },
          }}
        >
          <Typography variant="h3" sx={{ color: palette.title, fontWeight: 600, mt: { xs: 1, md: 0 } }}>
            Turnos en la liquidación
          </Typography>
          <Box
            sx={{
              overflow: "auto",
              minHeight: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pr: 0.5,
            }}
          >
            {turnos.length === 0 && (
              <Box
                sx={{
                  backgroundColor: palette.cardBg,
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: "12px",
                  p: 2,
                  color: palette.text,
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No hay turnos asociados</Typography>
                <Typography variant="body2" sx={{ color: palette.label }}>
                  Esta liquidación no tiene hojas de turno vinculadas.
                </Typography>
              </Box>
            )}

            {turnos.map((t) => (
              <Box
                key={t._id || t}
                sx={{
                  backgroundColor: palette.cardBg,
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: "12px",
                  p: 2.5,
                  minHeight: 132,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                  gridTemplateRows: "auto auto",
                  alignItems: "center",
                  rowGap: 1.25,
                  columnGap: 16,
                  boxShadow:
                    palette === PALETTE_LIGHT
                      ? "0 8px 22px rgba(2, 6, 23, 0.08)"
                      : "0 3px 10px rgba(0,0,0,0.24)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ gridColumn: "1 / 2" }}>
                  <Typography sx={{ color: palette.text, fontWeight: 700 }}>
                    Turno: {t._id || t}
                  </Typography>
                </Stack>

                <Chip
                  label={`€${Number(t.facturacionTotal || 0).toFixed(2)}`}
                  sx={{
                    justifySelf: { xs: "start", sm: "end" },
                    gridColumn: { xs: "1 / -1", sm: "2 / 3" },
                    bgcolor: "transparent",
                    border: `1px solid ${palette.accent}`,
                    color: palette.text,
                    fontWeight: 700,
                  }}
                />

                <Grid container spacing={1.5} sx={{ gridColumn: "1 / 2", width: "100%" }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ color: palette.label, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Fecha
                    </Typography>
                    <Typography sx={{ color: palette.text, fontWeight: 600 }}>
                      {t.fecha ? new Date(t.fecha).toLocaleDateString() : "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ color: palette.label, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Conductor
                    </Typography>
                    <Typography sx={{ color: palette.text, fontWeight: 600 }}>
                      {t.driverName || liquidacion.conductor || "—"}
                    </Typography>
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(`/turnos/${t._id || t}`)}
                  sx={{
                    gridColumn: { xs: "1 / -1", sm: "2 / 3" },
                    justifySelf: { xs: "stretch", sm: "end" },
                    mt: { xs: 0.5, sm: 0 },
                    backgroundColor: palette.btn,
                    color: "white",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { backgroundColor: palette.btnHover },
                  }}
                >
                  Ver Detalle
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LiquidacionDetalle;
